import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const emptyForm = {
    name: '',
    description: '',
    product_type: 'product',
    unit_price: '',
}

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
        )
        .join(' | ')
}

/* ─── component ───────────────────────────────────────────────────────────── */

const CreateProduct = () => {
    const navigate = useNavigate()
    const [formData, setFormData] = useState(emptyForm)
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setFormError(null)
        setSuccessMessage(null)

        const payload = { ...formData }

        try {
            await api.post('/products/', payload)
            setSuccessMessage('Product created successfully.')
            setTimeout(() => navigate('/products'), 1200)
        } catch (err) {
            console.error('Failed to create product', err)
            setFormError(getErrorMessage(err, 'Failed to create product. Please try again.'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Create Product</h4>
                            
                        </div>
                        <Link to="/products" className="btn btn-light">
                            <i className="ti ti-arrow-left me-1" />
                            Back to Products
                        </Link>
                    </div>

                    <form onSubmit={handleSubmit}>

                        {/* Alerts */}
                        {formError && (
                            <div className="alert alert-danger mb-4" role="alert">
                                <i className="ti ti-alert-circle me-2" />
                                {formError}
                            </div>
                        )}
                        {successMessage && (
                            <div className="alert alert-success mb-4" role="alert">
                                <i className="ti ti-check me-2" />
                                {successMessage}
                            </div>
                        )}

                        {/* Product details card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Product Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">

                                    {/* Name */}
                                    <div className="col-md-6">
                                        <label htmlFor="prod-name" className="form-label">
                                            Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="prod-name"
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            placeholder="e.g. Website Maintenance"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Type */}
                                    <div className="col-md-3">
                                        <label htmlFor="prod-type" className="form-label">
                                            Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="prod-type"
                                            name="product_type"
                                            className="form-select"
                                            value={formData.product_type}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="product">Product</option>
                                            <option value="service">Service</option>
                                        </select>
                                    </div>

                                    {/* Unit Price */}
                                    <div className="col-md-3">
                                        <label htmlFor="prod-price" className="form-label">
                                            Unit Price <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input
                                                id="prod-price"
                                                type="number"
                                                name="unit_price"
                                                className="form-control"
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                                value={formData.unit_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="col-12">
                                        <label htmlFor="prod-description" className="form-label">
                                            Description
                                        </label>
                                        <textarea
                                            id="prod-description"
                                            name="description"
                                            className="form-control"
                                            rows={4}
                                            placeholder="e.g. Monthly hosting and updates"
                                            value={formData.description}
                                            onChange={handleChange}
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                            <Link
                                to="/products"
                                className="btn btn-light"
                                onClick={(e) => submitting && e.preventDefault()}
                            >
                                Cancel
                            </Link>
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? <BeatLoader color="#fff" size={8} /> : 'Create Product'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default CreateProduct